/**
 * Pure utility functions for collapsing time gaps between recordings.
 *
 * Maps between real epoch time (with large gaps between recordings) and
 * collapsed visual time (gaps shrunk to a small fixed width).
 */

const DEFAULT_GAP_WIDTH_PERCENT = 0.02;
const DEFAULT_MIN_GAP_WIDTH = 1000;

/**
 * Merge overlapping or adjacent intervals. Input must be sorted by start.
 * Returns merged intervals with the id of the first recording in each merged group.
 */
function mergeIntervals(sorted) {
  if (sorted.length === 0) return [];

  const merged = [{ ...sorted[0] }];

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const last = merged[merged.length - 1];

    if (current.startTime <= last.endTime) {
      last.endTime = Math.max(last.endTime, current.endTime);
    } else {
      merged.push({ ...current });
    }
  }

  return merged;
}

/**
 * Build a collapsed timeline descriptor from an array of recordings.
 *
 * @param {Array<{id: string, startTime: number, endTime: number}>} recordings
 * @param {Object} [options]
 * @param {number} [options.gapWidthPercent=0.02] - Fraction of total recording duration for gap width
 * @param {number} [options.minGapWidth=1000] - Minimum gap width in ms
 * @returns {{ segments: Array, totalCollapsedDuration: number, gapWidth: number }}
 */
export function buildCollapsedTimeline(recordings, options = {}) {
  const empty = { segments: [], totalCollapsedDuration: 0, gapWidth: 0 };

  if (!recordings || recordings.length === 0) {
    return empty;
  }

  const {
    gapWidthPercent = DEFAULT_GAP_WIDTH_PERCENT,
    minGapWidth = DEFAULT_MIN_GAP_WIDTH,
  } = options;

  const sorted = [...recordings].sort((a, b) => a.startTime - b.startTime);
  const merged = mergeIntervals(sorted);

  if (merged.length === 0) {
    return empty;
  }

  const totalRecordingDuration = merged.reduce(
    (sum, rec) => sum + (rec.endTime - rec.startTime),
    0
  );

  const gapCount = merged.length - 1;
  const gapWidth =
    gapCount > 0
      ? Math.max(totalRecordingDuration * gapWidthPercent, minGapWidth)
      : 0;

  const segments = [];
  let collapsedOffset = 0;

  for (let i = 0; i < merged.length; i++) {
    const rec = merged[i];
    const duration = rec.endTime - rec.startTime;

    segments.push({
      realStart: rec.startTime,
      realEnd: rec.endTime,
      collapsedStart: collapsedOffset,
      collapsedEnd: collapsedOffset + duration,
      type: 'recording',
      recordingId: rec.id,
    });

    collapsedOffset += duration;

    if (i < merged.length - 1) {
      const nextRec = merged[i + 1];
      const realGapDuration = nextRec.startTime - rec.endTime;

      segments.push({
        realStart: rec.endTime,
        realEnd: nextRec.startTime,
        collapsedStart: collapsedOffset,
        collapsedEnd: collapsedOffset + gapWidth,
        type: 'gap',
        gapDuration: realGapDuration,
      });

      collapsedOffset += gapWidth;
    }
  }

  return {
    segments,
    totalCollapsedDuration: collapsedOffset,
    gapWidth,
  };
}

/**
 * Map a real epoch time to its collapsed position.
 *
 * @param {number} time - Real epoch time in ms
 * @param {{ segments: Array }} collapsedTimeline
 * @returns {number|null} Collapsed time or null if outside range
 */
export function realToCollapsed(time, collapsedTimeline) {
  const { segments } = collapsedTimeline;
  if (!segments || segments.length === 0) return null;

  for (const seg of segments) {
    if (seg.type === 'recording') {
      if (time >= seg.realStart && time <= seg.realEnd) {
        return seg.collapsedStart + (time - seg.realStart);
      }
    } else if (seg.type === 'gap') {
      if (time > seg.realStart && time < seg.realEnd) {
        const realGapDuration = seg.realEnd - seg.realStart;
        const collapsedGapDuration = seg.collapsedEnd - seg.collapsedStart;
        const fraction = (time - seg.realStart) / realGapDuration;
        return seg.collapsedStart + fraction * collapsedGapDuration;
      }
    }
  }

  return null;
}

/**
 * Map a collapsed position back to real epoch time.
 *
 * @param {number} collapsedTime - Position in collapsed time space
 * @param {{ segments: Array, totalCollapsedDuration: number }} collapsedTimeline
 * @returns {number|null} Real epoch time or null if outside range
 */
export function collapsedToReal(collapsedTime, collapsedTimeline) {
  const { segments } = collapsedTimeline;
  if (!segments || segments.length === 0) return null;
  if (collapsedTime < 0) return null;
  if (collapsedTime > collapsedTimeline.totalCollapsedDuration) return null;

  for (const seg of segments) {
    if (collapsedTime >= seg.collapsedStart && collapsedTime <= seg.collapsedEnd) {
      if (seg.type === 'recording') {
        return seg.realStart + (collapsedTime - seg.collapsedStart);
      }
      const collapsedGapDuration = seg.collapsedEnd - seg.collapsedStart;
      if (collapsedGapDuration === 0) return seg.realStart;
      const fraction = (collapsedTime - seg.collapsedStart) / collapsedGapDuration;
      const realGapDuration = seg.realEnd - seg.realStart;
      return seg.realStart + fraction * realGapDuration;
    }
  }

  return null;
}

/**
 * Where a real epoch time sits along the collapsed bar, as a percentage.
 *
 * Returns null when the time is outside every recording and every gap — a
 * marker left behind by a removed recording, say. Callers must skip those
 * rather than draw them: reporting 0 for "nowhere" piled every orphaned marker
 * onto the left edge of the timeline, where clicking one seeks to the start.
 *
 * @param {number} time - Real epoch time in ms
 * @param {{ segments: Array, totalCollapsedDuration: number }} collapsedTimeline
 * @returns {number|null} 0-100, or null if the time is off the timeline
 */
export function collapsedPercent(time, collapsedTimeline) {
  const total = collapsedTimeline?.totalCollapsedDuration ?? 0;
  if (total <= 0) return null;
  const collapsed = realToCollapsed(time, collapsedTimeline);
  if (collapsed === null) return null;
  return (collapsed / total) * 100;
}

/**
 * Check whether a real epoch time falls in a gap between recordings.
 *
 * @param {number} time - Real epoch time in ms
 * @param {{ segments: Array }} collapsedTimeline
 * @returns {boolean}
 */
export function isInGap(time, collapsedTimeline) {
  const segment = getSegmentAt(time, collapsedTimeline);
  return segment !== null && segment.type === 'gap';
}

/**
 * Return the segment (recording or gap) that contains the given real time.
 *
 * @param {number} time - Real epoch time in ms
 * @param {{ segments: Array }} collapsedTimeline
 * @returns {Object|null} The matching segment or null
 */
export function getSegmentAt(time, collapsedTimeline) {
  const { segments } = collapsedTimeline;
  if (!segments || segments.length === 0) return null;

  for (const seg of segments) {
    if (seg.type === 'recording') {
      if (time >= seg.realStart && time <= seg.realEnd) {
        return seg;
      }
    } else if (seg.type === 'gap') {
      if (time > seg.realStart && time < seg.realEnd) {
        return seg;
      }
    }
  }

  return null;
}

/**
 * Snap a real time that falls inside a gap to the nearest recording boundary.
 *
 * If the time is in the first half of the gap, snap to the end of the preceding
 * recording. If in the second half, snap to the start of the following recording.
 * Returns the time unchanged if it is not inside a gap.
 *
 * @param {number} time - Real epoch time in ms
 * @param {{ segments: Array }} collapsedTimeline
 * @returns {number} Snapped real time (or original if not in a gap)
 */
export function snapToNearestRecordingBoundary(time, collapsedTimeline) {
  const segment = getSegmentAt(time, collapsedTimeline);
  if (!segment || segment.type !== 'gap') return time;

  const distToStart = time - segment.realStart;
  const distToEnd = segment.realEnd - time;
  return distToStart <= distToEnd ? segment.realStart : segment.realEnd;
}
